/* eslint-disable no-console */
/* eslint-disable no-plusplus */
import { useCallback, useEffect, useState } from "react";

import {
  fetchConfig,
  setCredit,
  setGameType,
  setUserInfo,
} from "../store/config-slice";
import { useAppDispatch } from "../store/hooks";

import {
  checkProdURLParams,
  getParamsFromURL,
  checkDemoUrlParams,
  getGameTypeFromPath,
  getTempTokenFromURL,
} from "../utils/SignInUtils";

import { GameTypeEnum, RequestStatus } from "../enums";

import { ILoadData } from "../interfaces";

import { initAxios } from "../services/axios";
import { signInOnAleaOnline } from "../services/middlewareCommunication";
import { ApiConsumer } from "../services/ApiConsumer";

import { BASE_URL } from "../config/environment";

const sleep = (time: number): Promise<unknown> =>
  new Promise((resolve) => setTimeout(resolve, time));

let configPullAttempt = 0;
const ALLOWED_CONFIG_PULL_ATTEMPTS = 3;

export const useLoad = (): ILoadData => {
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const dispatch = useAppDispatch();

  const handleErrorModal = (newMessage: string): void => {
    setIsLoading(true);
    setMessage(newMessage);
  };

  const pullConfig = useCallback(async (): Promise<boolean> => {
    let isConfFetched = false;

    while (configPullAttempt++ < ALLOWED_CONFIG_PULL_ATTEMPTS) {
      // eslint-disable-next-line no-await-in-loop
      const payloadAction = await dispatch(fetchConfig());

      isConfFetched =
        payloadAction.meta.requestStatus !== RequestStatus.REJECTED;

      if (isConfFetched) {
        break;
      }

      // eslint-disable-next-line no-await-in-loop
      await sleep(2000);
    }

    return isConfFetched;
  }, [dispatch]);

  const setReauthError = useCallback((): void => {
    handleErrorModal("error.problemReload");
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const gameType = getGameTypeFromPath();

        dispatch(setGameType(gameType));

        if (gameType === GameTypeEnum.ONLINE) {
          const urlParams = getParamsFromURL();

          if (checkProdURLParams(urlParams) || checkDemoUrlParams(urlParams)) {
            const response = await signInOnAleaOnline(urlParams);

            dispatch(
              setUserInfo({
                username: response.userName,
                email: response.email,
              })
            );

            dispatch(
              setCredit({
                balance: response.balance,
                currency: response.currency,
              })
            );

            const bergmann = response.games.find(
              (game) => game.gameName === "BergmannRoulette"
            );

            if (bergmann) {
              initAxios(
                bergmann.endpoint,
                bergmann.token,
                bergmann.refreshToken,
                setReauthError
              );

              const isConfFetched = await pullConfig();

              if (isConfFetched) {
                setIsLoading(false);
              } else {
                handleErrorModal("error.problemReload");
              }
            } else {
              console.error("Bergmann not found.");

              handleErrorModal("error.problemReload");
            }
          } else {
            handleErrorModal("error.paramsErr");
          }
        } else if (gameType === GameTypeEnum.PANEL) {
          const tempToken = getTempTokenFromURL();

          if (tempToken) {
            const {
              accessToken,
              refreshToken,
            } = await ApiConsumer.getTokensForLandbase(BASE_URL, tempToken);

            initAxios(
              BASE_URL,
              accessToken,
              refreshToken,
              setReauthError
            );

            const isConfFetched = await pullConfig();

            if (isConfFetched) {
              setIsLoading(false);
            } else {
              handleErrorModal("error.problemReload");
            }
          } else {
            handleErrorModal("error.problemReload");
          }
        } else if (gameType === GameTypeEnum.PRESENTATION) {
          setIsLoading(false);
        }
      } catch (e) {
        console.error(e);
        handleErrorModal("error.problemReload");
      }
    })();
  }, [dispatch, pullConfig, setReauthError]);

  return { isLoading, message };
};
